import aiChatbotApiService from './api';
import type {
  UserAiSkillRecord,
  AiSkillInstanceRecord,
  SkillOption,
} from '../types/skillAsset';

/** Directus /items 返回结构 */
interface DirectusListResponse<T> {
  readonly data: readonly T[];
}

/**
 * 获取当前用户在指定能力下可用的技能列表
 *
 * 查询逻辑：
 * 1. 查 user_ai_skills 表 → 拿到当前用户所有 active 的 production_id
 * 2. 查 ai_skill_instances 表 → 用 production_id + capability_id 过滤
 * 3. 取交集后返回 skill_name + mind_skill_id 作为下拉项
 *
 * @param capabilityId - 当前 dify 能力的数据库 id（ai_dify_capability.id）
 */
export const fetchSkillAssets = async (
  capabilityId: number
): Promise<readonly SkillOption[]> => {
  if (!capabilityId) {
    console.warn('[difyAssetsService] 缺少 capabilityId');
    return [];
  }

  try {
    const userId = sessionStorage.getItem("agimx_user_id");
    // 1. 查询当前用户已拥有的 active 技能
    const userSkillsRes = await aiChatbotApiService.get<DirectusListResponse<UserAiSkillRecord>>(
      '/items/user_ai_skills',
      {
        params: {
          filter: {
            user_id: { _eq: userId },
            status: { _eq: 'active' },
          },
          fields: ['id', 'production_id'],
          limit: -1,
        },
      }
    );

    const userSkills: readonly UserAiSkillRecord[] = userSkillsRes?.data ?? [];

    if (userSkills.length === 0) {
      return [];
    }

    // 收集用户拥有的所有 mind_skill_id
    const userMindSkillIds: readonly string[] = userSkills.map(
      (s: UserAiSkillRecord): string => s.production_id
    );

    // 2. 查询该能力下的技能实例（同时过滤用户已拥有的）
    const instancesRes = await aiChatbotApiService.get<DirectusListResponse<AiSkillInstanceRecord>>(
      '/items/ai_skill_instances',
      {
        params: {
          filter: {
            _and: [
              { mind_capability_id: { _eq: capabilityId } },
              { production_id: { _in: userMindSkillIds } },
            ],
          },
          fields: ['id', 'skill_name', 'mind_skill_id'],
          limit: -1,
        },
      }
    );

    const instances: readonly AiSkillInstanceRecord[] = instancesRes?.data ?? [];

    // 3. 映射为下拉选项
    return instances.map(
      (inst: AiSkillInstanceRecord): SkillOption => ({
        id: inst.mind_skill_id,
        name: inst.skill_name,
      })
    );
  } catch (error) {
    console.error('[difyAssetsService] 获取技能列表失败:', error);
    return [];
  }
};